import { apiClient } from "@copro/api";
import { DocumentType } from "../../../types";
import { deleteDriveFilesQuietly } from "./driveFiles";

type OwnerColumn = 'provider_id' | 'meeting_id';

type SyncParams = {
  // Existing linked document, or null when the owner has no document yet.
  currentDocumentId: string | null;
  // Desired file URL: a value creates/updates the document, null removes it.
  fileUrl: string | null;
  name: string;
  type: DocumentType;
  ownerColumn: OwnerColumn;
  ownerId: string;
  spaceId: string;
  // Runs right before an existing document row is deleted, so the caller can
  // drop any FK still pointing at it (provider_contracts.document_id is
  // ON DELETE RESTRICT and would otherwise block the delete).
  onBeforeDelete?: () => Promise<void>;
};

async function findDocumentType(
  spaceId: string,
  type: DocumentType,
  isMeeting: boolean
): Promise<{ id: string } | null> {
  const { data: exactMatch } = await apiClient
    .from('document_types')
    .select('id')
    .eq('space_id', spaceId)
    .ilike('name', type)
    .limit(1)
    .maybeSingle();

  if (exactMatch) return exactMatch;

  if (isMeeting) {
    const { data: fallback } = await apiClient
      .from('document_types')
      .select('id')
      .eq('space_id', spaceId)
      .or('name.ilike.Réunion CS,name.ilike."PV d%AG",name.ilike.Autre')
      .limit(1)
      .maybeSingle();
    return fallback;
  }

  return null;
}

// Keeps a single owned document (a contract file, a meeting report, ...) in sync
// with its owner row and cleans up the underlying Drive file when it is replaced
// or removed. Returns the resulting document id (null once removed).
export async function syncOwnedDocument(params: SyncParams): Promise<string | null> {
  const ownerFields =
    params.ownerColumn === 'provider_id'
      ? { provider_id: params.ownerId }
      : { meeting_id: params.ownerId };

  if (params.currentDocumentId) {
    const { data: existing } = await apiClient
      .from('documents')
      .select('file_url')
      .eq('id', params.currentDocumentId)
      .single();

    if (params.fileUrl) {
      // Replace: drop the previous Drive file only when the URL actually changed.
      if (existing?.file_url && existing.file_url !== params.fileUrl) {
        await deleteDriveFilesQuietly([existing.file_url], params.spaceId);
      }

      const documentType = await findDocumentType(
        params.spaceId,
        params.type,
        params.ownerColumn === 'meeting_id'
      );

      const updateData: { name: string; file_url: string; document_type_id?: string | null } = {
        name: params.name,
        file_url: params.fileUrl,
      };
      if (documentType?.id) {
        updateData.document_type_id = documentType.id;
      }

      const { error } = await apiClient
        .from('documents')
        .update(updateData)
        .eq('id', params.currentDocumentId);

      if (error) throw error;

      return params.currentDocumentId;
    }

    // Remove: unlink the owner first (RESTRICT), then delete file + row.
    if (params.onBeforeDelete) await params.onBeforeDelete();
    await deleteDriveFilesQuietly([existing?.file_url], params.spaceId);

    const { error } = await apiClient
      .from('documents')
      .delete()
      .eq('id', params.currentDocumentId);

    if (error) throw error;

    return null;
  }

  if (params.fileUrl) {
    const documentType = await findDocumentType(
      params.spaceId,
      params.type,
      params.ownerColumn === 'meeting_id'
    );

    const { data, error } = await apiClient
      .from('documents')
      .insert({
        name: params.name,
        file_url: params.fileUrl,
        document_type_id: documentType?.id ?? null,
        ...ownerFields,
        space_id: params.spaceId,
      })
      .select('id')
      .single();

    if (error) throw error;

    return data.id;
  }

  return null;
}
